import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bike,
  BookOpenText,
  ChefHat,
  FileBarChart,
  LogOut,
  Monitor,
  Package,
  Settings,
  Shield,
  User as UserIcon,
} from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { UserRole } from '../types';

export const Navbar = () => {
  const { user, logout, leaveActiveAccess, currentCompany } = useStore();
  const location = useLocation();
  const [showExitOptions, setShowExitOptions] = useState(false);

  if (!user) return null;

  const isAdmin = user.role === UserRole.SAAS_ADMIN;
  const isAccessSelection = location.pathname === '/access';
  const subLoginEnabled = !isAdmin && currentCompany?.useSubLogin === true;
  const isActive = (path: string) => location.pathname.includes(path);
  const companyName = isAdmin ? 'Painel Admin' : currentCompany?.name || 'FlowTab';
  const homePath = isAdmin ? '/admin' : '/pos';
  const subtitle = isAdmin
    ? 'Administração FlowTab'
    : isAccessSelection
      ? 'Seleção de acesso'
      : 'Controle de comandas';
  const roleLabel = isAdmin
    ? 'SAAS Admin'
    : user.role === UserRole.OWNER
      ? 'Dono'
      : user.role === UserRole.CASHIER
        ? 'Caixa'
        : 'Garçom';
  const initials = (user.name || 'FT')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

  const navButtonClass = (path: string) =>
    `inline-flex shrink-0 items-center gap-1.5 rounded-xl px-2 py-2 text-xs font-black transition-all lg:gap-2 lg:rounded-2xl lg:px-4 lg:py-2.5 lg:text-sm ${
      isActive(path)
        ? 'bg-white text-slate-950 shadow-[0_14px_30px_rgba(15,23,42,0.18)]'
        : 'text-slate-300 hover:bg-white/10 hover:text-white'
    }`;

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950 text-white shadow-[0_18px_50px_rgba(2,6,23,0.32)]">
      <style>{`@keyframes flowtab-neon-shift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}@media(prefers-reduced-motion:reduce){.flowtab-neon-line{animation:none!important}}`}</style>
      <div className="flowtab-neon-line h-1 bg-[linear-gradient(90deg,#34d399_0%,#22d3ee_28%,#3b82f6_50%,#22d3ee_72%,#34d399_100%)] bg-[length:300%_100%]" style={{ animation: 'flowtab-neon-shift 7s ease-in-out infinite', boxShadow: '0 0 7px rgba(34, 211, 238, 0.78), 0 0 16px rgba(16, 185, 129, 0.32)' }} />

      <div className="mx-auto flex max-w-[1920px] items-center justify-between gap-2 px-2 py-2.5 sm:gap-3 sm:px-3 lg:px-6 lg:py-3">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3 lg:gap-4">
          <Link
            to={homePath}
            className="group flex h-12 w-24 shrink-0 items-center justify-center overflow-visible transition hover:-translate-y-0.5 sm:w-32 lg:h-14 lg:w-44"
            title="FlowTab"
          >
            <img
              src="/flowtab-logo-dark-transparent.png"
              alt="FlowTab"
              className="h-8 w-32 max-w-none object-contain object-center opacity-95 drop-shadow-[0_10px_22px_rgba(45,212,191,0.22)] brightness-0 invert transition group-hover:opacity-100 group-hover:drop-shadow-[0_12px_28px_rgba(45,212,191,0.35)] sm:h-9 sm:w-40 lg:h-11 lg:w-48"
              style={{ transform: 'translateX(-10px)' }}
            />
          </Link>

          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="max-w-[115px] truncate text-base font-black tracking-tight text-white sm:max-w-[150px] sm:text-lg lg:max-w-[280px] lg:text-2xl">
                {companyName}
              </h1>
              <span className={`hidden rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ring-1 md:inline-flex ${
                isAdmin
                  ? 'bg-cyan-400/15 text-cyan-100 ring-cyan-300/25'
                  : 'bg-emerald-400/15 text-emerald-100 ring-emerald-300/25'
              }`}>
                {isAdmin ? 'Plataforma' : isAccessSelection ? 'Acesso seguro' : 'Online'}
              </span>
            </div>
            <p className="hidden truncate text-[10px] font-black uppercase tracking-[0.18em] text-cyan-100/55 lg:block lg:text-xs">
              {subtitle}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center justify-end gap-2 lg:gap-3">
          {!isAccessSelection && user.role !== UserRole.WAITER && (
            <div className="hidden min-w-0 items-center gap-0.5 rounded-2xl bg-white/8 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_30px_rgba(0,0,0,0.18)] md:flex lg:gap-1.5 lg:rounded-[1.45rem] lg:p-1.5">
              {isAdmin && (
                <Link to="/admin" className="shrink-0">
                    <button className={navButtonClass('/admin')} title="Painel Admin">
                    <Shield className="h-4 w-4" /> <span className="hidden xl:inline">Painel Admin</span>
                  </button>
                </Link>
              )}

              {(user.role === UserRole.OWNER || user.role === UserRole.CASHIER) && (
                <>
                  <Link to="/pos" className="shrink-0">
                    <button className={navButtonClass('/pos')} title="PDV">
                      <Monitor className="h-4 w-4" /> <span className="hidden xl:inline">PDV</span>
                    </button>
                  </Link>

                  <Link to="/menu" className="shrink-0">
                    <button className={navButtonClass('/menu')} title="Produtos">
                      <BookOpenText className="h-4 w-4" /> <span className="hidden xl:inline">Produtos</span>
                    </button>
                  </Link>

                  {currentCompany?.useStockManagement && (
                    <Link to="/stock" className="shrink-0">
                      <button className={navButtonClass('/stock')} title="Estoque">
                        <Package className="h-4 w-4" /> <span className="hidden xl:inline">Estoque</span>
                      </button>
                    </Link>
                  )}

                  <Link to="/reports" className="shrink-0">
                    <button className={navButtonClass('/reports')} title="Relatórios">
                      <FileBarChart className="h-4 w-4" /> <span className="hidden xl:inline">Relatórios</span>
                    </button>
                  </Link>

                  {currentCompany?.useKitchenDisplay && (
                    <Link to="/kitchen" className="shrink-0 max-xl:hidden">
                      <button className={navButtonClass('/kitchen')} title="Cozinha">
                        <ChefHat className="h-4 w-4" /> <span className="hidden xl:inline">Cozinha</span>
                      </button>
                    </Link>
                  )}

                  {currentCompany?.useDelivery === true && (
                    <Link to="/delivery" className="shrink-0 max-xl:hidden">
                      <button className={navButtonClass('/delivery')} title="Delivery">
                        <Bike className="h-4 w-4" /> <span className="hidden xl:inline">Delivery</span>
                      </button>
                    </Link>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex shrink-0 items-center gap-0.5 rounded-2xl bg-gradient-to-r from-white/12 to-white/6 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_14px_35px_rgba(0,0,0,0.20)] sm:gap-1 sm:rounded-[1.45rem] sm:p-1.5">
            {isAccessSelection ? (
              <div className="hidden items-center gap-2 rounded-2xl border border-emerald-300/15 bg-emerald-400/5 px-3 py-2 text-left sm:flex">
                <Shield className="h-4 w-4 shrink-0 text-emerald-300" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-200">Ambiente protegido</p>
                  <p className="max-w-[150px] truncate text-xs font-bold text-white">Conta da empresa conectada</p>
                </div>
              </div>
            ) : (
              <>
                <Link to="/settings" title="Configurações da conta" className="hidden sm:block">
                  <div className="flex items-center gap-2 rounded-2xl py-1 pl-1 pr-2.5 transition hover:bg-white/10 lg:gap-3 lg:pr-3.5">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-black text-slate-950 shadow-[0_8px_20px_rgba(0,0,0,0.18)] sm:h-9 sm:w-9 sm:text-xs ${
                      isAdmin ? 'bg-cyan-100' : 'bg-emerald-100'
                    }`}>
                      {initials}
                    </div>
                    <div className="hidden text-left lg:block">
                      <div className="max-w-[118px] truncate text-sm font-black leading-tight text-white">{user.name}</div>
                      <div className="max-w-[118px] truncate text-[10px] font-black uppercase tracking-wide text-cyan-100/55">{roleLabel}</div>
                    </div>
                  </div>
                </Link>

                <Link to="/settings" className="rounded-full p-2.5 text-white/70 transition hover:bg-white/10 hover:text-white sm:hidden">
                  <UserIcon className="h-5 w-5" />
                </Link>

                <Link to="/settings" className="rounded-full p-2.5 text-white/70 transition hover:bg-white/10 hover:text-white" title="Configurações">
                  <Settings className="h-5 w-5" />
                </Link>
              </>
            )}

            <button
              onClick={() => subLoginEnabled ? setShowExitOptions(true) : logout()}
              className="rounded-full p-2.5 text-white/70 transition hover:bg-red-500/15 hover:text-red-100"
              title="Sair"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      {subLoginEnabled && showExitOptions && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[90] cursor-default"
            aria-label="Fechar opções de saída"
            onClick={() => setShowExitOptions(false)}
          />
          <section className="fixed right-3 top-[72px] z-[100] w-[calc(100vw-1.5rem)] max-w-[336px] overflow-hidden rounded-2xl border border-white/10 bg-[#242424] text-white shadow-[0_22px_55px_rgba(0,0,0,0.48)] sm:right-5 sm:top-[82px]">
            <div className="flex items-center gap-3 px-4 py-4">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-black text-slate-950 ${isAdmin ? 'bg-cyan-100' : 'bg-emerald-100'}`}>
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-white">{user.name}</p>
                <p className="mt-0.5 truncate text-xs text-slate-300">{companyName} · {roleLabel}</p>
                <p className="mt-1 text-[11px] font-bold text-emerald-300">Sessão em uso neste dispositivo</p>
              </div>
            </div>

            <div className="border-t border-white/10 py-2">
              <button
                type="button"
                onClick={() => { setShowExitOptions(false); leaveActiveAccess(); }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-white/10"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-400/12 text-emerald-200">
                  <UserIcon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">Sair somente desta pessoa</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-slate-300">Volta para a seleção e mantém a empresa conectada.</span>
                </span>
              </button>
            </div>

            <div className="border-t border-white/10 py-2">
              <button
                type="button"
                onClick={() => { setShowExitOptions(false); logout(); }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-red-500/15"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-500/12 text-red-200">
                  <LogOut className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-red-100">Sair geral</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-slate-300">Desconecta a conta da empresa deste computador.</span>
                </span>
              </button>
            </div>
          </section>
        </>
      )}
    </nav>
  );
};
